import {Badge, Box, Button, Collapse, Divider, IconButton, Stack, Tooltip} from "@mui/material";
import axios from "../../../lib/axios";
import {useEffect, useMemo, useRef, useState} from "react";
import {ActionButton, SimpleSearchBar} from "src/components/common";
import {ComplexFilter, ComplexFilterContextProvider} from "../complex-filter";
import encodeFilterGroups from "../complex-filter/encodeFilterGroups";
import SavedFiltersMenu from "./SavedFiltersMenu";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListTwoToneIcon from '@mui/icons-material/FilterListTwoTone';
import ManageSearch from '@mui/icons-material/ManageSearch'
import DeviceSearchForm from "../../device/DeviceSearchForm";
import DeviceSearchDownloadButton from "../../device/DeviceSearchDownloadButton";
import {useSnackbar} from "notistack";
import {useQuery} from '@tanstack/react-query';
import {getSearchFile} from "../../../api/queries";
import DownloadIcon from "../../../icons/Download";
import PermissionGate from "../../../permission-management/PermissionGate";

const CustomizableListSearch = ({
                                  responsive = false,
                                  disableSearch,
                                  disableComplexFilter,
                                  advancedSearch,
                                  toggleSearch,
                                  setToggleSearch,
                                  searchParams,
                                  setSearchParams,
                                  setFilterParams,
                                  filterHeaders,
                                  openCollapse,
                                  setOpenCollapse,
                                  filterGroups,
                                  setFilterGroups,
                                  setBodyParams,
                                  setPage,
                                  bodyParams,
                                  queryParams,
                                  pageKey,
                                  exportUrl,
                                  advancedFormValues,
                                  setAdvancedFormValues,
                                  pinnedVersion,
                                }) => {
  const [internalFilterParams, setInternalFilterParams] = useState('');
  // Bumped each time a saved preset is loaded — used as a key to remount the
  // ComplexFilterContextProvider so its init effect runs with the new
  // `initialFilterGroups` (the provider's effect has a [] dep array).
  const [presetVersion, setPresetVersion] = useState(0);
  // Live filter panel state (updates on every keystroke) — distinct from the
  // applied `filterGroups` history that only updates when the user clicks
  // Apply. Used to enable the Save button as soon as values are typed.
  const [liveFilterGroups, setLiveFilterGroups] = useState({});

  // Count of applied filters — shown as the badge on the Filter button.
  const activeFilterCount = useMemo(() => {
    if (!filterGroups) return 0;
    let total = 0;
    Object.values(filterGroups).forEach((group) => {
      Object.values(group.filters || {}).forEach((f) => {
        const hasValue = f.value !== null && f.value !== undefined && f.value !== '';
        if (f.active && hasValue) total += 1;
      });
    });
    return total;
  }, [filterGroups]);

  // Count of filters currently typed into the panel (whether or not the user
  // has clicked Apply). Drives the Save button's enabled state.
  const liveFilterCount = useMemo(() => {
    if (!liveFilterGroups) return 0;
    let total = 0;
    Object.values(liveFilterGroups).forEach((group) => {
      Object.values(group.filters || {}).forEach((f) => {
        const hasValue = f.value !== null && f.value !== undefined && f.value !== '';
        if (f.active && hasValue) total += 1;
      });
    });
    return total;
  }, [liveFilterGroups]);

  // Same encoding as ComplexFilterContextProvider's filterGroups effect —
  // used to apply seeded/saved filters without going through the panel UI.
  const filterGroupsToQueryString = (groups) => encodeFilterGroups(groups);

  // On first mount, if the caller seeded `filterGroups` (a Period or Model
  // chip on the Devices page — each Period click remounts the list — or a
  // chart drill-down from the Overview page handing us `?status=2`), apply
  // the encoded params immediately. The panel stays collapsed: the Filter
  // button's count badge already shows that filters are active, and popping
  // the panel open on every chip click pushed the list down the page — the
  // user expands it when they want to edit the rows.
  // Intentionally runs once: subsequent filterGroups changes go through the
  // panel's Apply button, saved-preset onLoad or pinned merges, not this
  // effect.
  const didApplyInitial = useRef(false);
  useEffect(() => {
    if (didApplyInitial.current) return;
    if (filterGroups && Object.keys(filterGroups).length > 0) {
      didApplyInitial.current = true;
      setFilterParams(filterGroupsToQueryString(filterGroups));
    }
  }, []);

  // One-shot: an advanced search restored from the URL (persistStateInUrl)
  // arrives with `bodyParams` already seeded — open the Advanced Search panel
  // so the active criteria aren't invisible.
  const didOpenRestoredAdvanced = useRef(false);
  useEffect(() => {
    if (didOpenRestoredAdvanced.current) return;
    didOpenRestoredAdvanced.current = true;
    if (advancedSearch && bodyParams && Object.keys(bodyParams).length > 0) {
      setToggleSearch(true);
    }
  }, []);

  const advancedSearchActive = !!(bodyParams && Object.keys(bodyParams).length > 0);

  if (disableSearch && !filterHeaders && !advancedSearch) {
    return null;
  }

  const handleCollapseToggle = () => {setOpenCollapse(!openCollapse); setToggleSearch(false); };

  const handleAdvancedSearch = () => {setToggleSearch(!toggleSearch); setOpenCollapse(false)};

  const { enqueueSnackbar: enqueueExportSnackbar } = useSnackbar();
  const [exporting, setExporting] = useState(false);

  // Fire the export endpoint with the same query string the list is using.
  // We hit it as a blob and trigger a download by spawning an anchor —
  // same pattern as the per-page download buttons, but generic so any
  // list pulling its data from a ViewSet that mixes in CsvExportMixin
  // can opt in by just passing `exportUrl`.
  const handleExport = async () => {
    if (!exportUrl) return;
    setExporting(true);
    try {
      const suffix = queryParams ? `?${queryParams}` : '';
      const response = await axios.get(`${exportUrl}${suffix}`, { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'export.csv';
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      enqueueExportSnackbar(
        'Export failed. Try narrowing the filter or contact an admin.',
        { anchorOrigin: { horizontal: 'right', vertical: 'top' }, variant: 'error' },
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack>
      {
          <Box sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            flexWrap: responsive ? 'wrap' : undefined,
            justifyContent: 'flex-end',
            gap: 1.5,
            alignItems: 'center'
        }}>
            {
                !disableSearch && (
                    <Box sx={responsive ? { flex: '1 1 360px', minWidth: 0 } : { display: 'contents' }}>
                    <SimpleSearchBar
                        compact={responsive}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                        setPage={setPage}
                    />
                    </Box>
                )
            }
            {
                !disableComplexFilter && filterHeaders && pageKey && (
                    <SavedFiltersMenu
                        pageKey={pageKey}
                        currentFilterGroups={liveFilterCount > 0 ? liveFilterGroups : filterGroups}
                        hasActiveFilters={liveFilterCount > 0 || activeFilterCount > 0}
                        onLoad={(preset) => {
                            setFilterGroups(preset);
                            setPresetVersion((v) => v + 1);
                            setOpenCollapse(true);
                            // Apply immediately — reset to page 0 and push the
                            // encoded params straight to the outer setter so
                            // the user doesn't have to click "Apply".
                            setPage(0);
                            setFilterParams(filterGroupsToQueryString(preset));
                        }}
                    />
                )
            }
            {
                exportUrl && (
                    <Tooltip title="Export the current filtered list as CSV">
                        <span>
                            <IconButton
                                onClick={handleExport}
                                disabled={exporting}
                                size="medium"
                                color="primary"
                                aria-label="Export CSV"
                            >
                                <FileDownloadIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            }
            {
                !disableComplexFilter && filterHeaders && (
                    <Badge
                        badgeContent={activeFilterCount}
                        color="error"
                        overlap="rectangular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Button
                            variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                            color="primary"
                            startIcon={<FilterListTwoToneIcon fontSize="large"/>}
                            onClick={handleCollapseToggle}
                            size="medium"
                        >
                            Filter
                        </Button>
                    </Badge>
                )
            }
            {
                advancedSearch && (
                    <Badge
                        variant="dot"
                        color="error"
                        invisible={!advancedSearchActive}
                        overlap="rectangular"
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<ManageSearch fontSize="large"/>}
                            onClick={handleAdvancedSearch}
                            size="medium"
                        >
                            Advanced Search
                        </Button>
                    </Badge>
                )
            }
        </Box>
      }
      {
        filterHeaders && (
          <Box sx={{ px: 1.5, pb: 0.5 }}>
            <Collapse in={openCollapse} timeout="auto">
              <ComplexFilterContextProvider
                key={`complex-filter-${presetVersion}-${pinnedVersion ?? 0}`}
                initialFilterGroups={filterGroups}
                filterHeaders={filterHeaders}
                setFilterQueryParams={setInternalFilterParams}
                onLiveChange={setLiveFilterGroups}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start"
                  }}
                >
                  <ComplexFilter
                    internalFilterParams={internalFilterParams}
                    setFilterParams={setFilterParams}
                    setInitialFilterGroups={setFilterGroups}
                    setPage={setPage}
                    setBodyParams={setBodyParams}
                  />
                </Box>
              </ComplexFilterContextProvider>
              <Divider/>
            </Collapse>
          </Box>
        )
      }
      {
        advancedSearch && (
        <Box sx={{ mt: 1.5, mb: 1.5 }}>
          <Collapse in={toggleSearch} timeout="auto">
            <Box sx={{gap:2, display:'flex', flexDirection:'column'}}>
              <DeviceSearchForm
                setBodyParams={setBodyParams}
                                setPage={setPage}
                setFilterParams={setFilterParams}
                bodyParams={bodyParams}
                queryParams={queryParams}
                initialValues={advancedFormValues}
                onApplied={setAdvancedFormValues}
              />
              <PermissionGate
                permissions={'commonlib.download_device_search'}
              >
                <Box sx={{display:'flex',justifyContent:'end'}}>
                  <DeviceSearchDownloadButton
                    queryParams={queryParams}
                    bodyParams={bodyParams}
                  />
                </Box>
              </PermissionGate>
            </Box>
          </Collapse>
        </Box>
        )
      }
    </Stack>
  );
};

export default CustomizableListSearch;
